export const COMPLIANCE_COOKIE = "dtection-compliance";
export const COMPLIANCE_CHANGE_KEY = "dtection-compliance-change";
export const TERMS_VERSION = "1";

const CHANGE_EVENT = "dtection-compliance-change";

interface Acceptance {
  principal: string;
  version: string;
}

const cookieAttributes = () =>
  `Path=/; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;

export const hasAcceptedCompliance = (
  principal: string | undefined,
): boolean => {
  if (!principal) return false;
  try {
    const cookie = document.cookie
      .split("; ")
      .find((value) => value.startsWith(`${COMPLIANCE_COOKIE}=`));
    if (!cookie) return false;
    const record: unknown = JSON.parse(
      decodeURIComponent(
        atob(
          cookie
            .slice(COMPLIANCE_COOKIE.length + 1)
            .replace(/-/g, "+")
            .replace(/_/g, "/"),
        ),
      ),
    );
    return (
      typeof record === "object" &&
      record !== null &&
      "principal" in record &&
      record.principal === principal &&
      "version" in record &&
      record.version === TERMS_VERSION
    );
  } catch {
    return false;
  }
};

const notifyChange = (reset: boolean) => {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: reset }));
  try {
    localStorage.setItem(
      COMPLIANCE_CHANGE_KEY,
      JSON.stringify({ reset, nonce: `${Date.now()}-${Math.random()}` }),
    );
  } catch {
    return false;
  }
  return true;
};

export const acceptCompliance = (principal: string | undefined): boolean => {
  if (!principal) return false;
  const record: Acceptance = { principal, version: TERMS_VERSION };
  try {
    const value = btoa(encodeURIComponent(JSON.stringify(record)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    document.cookie = `${COMPLIANCE_COOKIE}=${value}; ${cookieAttributes()}`;
  } catch {
    return false;
  }
  const accepted = hasAcceptedCompliance(principal);
  if (accepted) notifyChange(false);
  return accepted;
};

export const clearCompliance = () => {
  try {
    document.cookie = `${COMPLIANCE_COOKIE}=; Max-Age=0; ${cookieAttributes()}`;
  } catch {
    notifyChange(true);
    return;
  }
  notifyChange(true);
};

export const subscribeToCompliance = (
  onChange: (reset: boolean) => void,
): (() => void) => {
  const onLocalChange = (event: Event) => {
    onChange(event instanceof CustomEvent && event.detail === true);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== COMPLIANCE_CHANGE_KEY || !event.newValue) return;
    try {
      const signal: unknown = JSON.parse(event.newValue);
      onChange(
        typeof signal === "object" &&
          signal !== null &&
          "reset" in signal &&
          signal.reset === true,
      );
    } catch {
      onChange(false);
    }
  };
  const onFocus = () => onChange(false);
  const onVisibility = () => {
    if (document.visibilityState === "visible") onChange(false);
  };
  window.addEventListener(CHANGE_EVENT, onLocalChange);
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onLocalChange);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisibility);
  };
};
